import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';

import { authApi, AuthApiError } from '../../api/authApi';
import { ME_QUERY_KEY } from '../../queries/useMe';
import { useAuthStore } from '../../store/useAuthStore';
import { useFingerprintStore } from '../../store/useFingerprintStore';

export function GuestTab(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const closeModal = useAuthStore((s) => s.closeAuthModal);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const fingerprintHex = useFingerprintStore.getState().hash_hex;
      const data = await authApi.guest(fingerprintHex ? { fingerprint_hash: fingerprintHex } : undefined);
      setUser(
        {
          userId: data.user_id,
          nickname: data.nickname ?? null,
          email: null,
          authMethod: 'anonymous',
          tierId: 1,
          tierKey: 'guest',
          ageVerificationStatus: 'pending',
        },
        data.csrf_token,
      );
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      closeModal();
      navigate('/match');
    } catch (err) {
      setError(err instanceof AuthApiError ? err.message : 'Could not start a guest session');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        No account needed. We'll generate a random nickname and you can start matching immediately. Clearing your
        cookies will end the guest session.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      <Button variant="contained" size="large" onClick={start} disabled={loading} fullWidth>
        {loading ? 'Starting…' : 'Continue as Guest'}
      </Button>
    </Box>
  );
}
